import React, { useEffect, useRef, useState } from 'react';

/** How far the row slides aside, and how much of that counts as intent. */
const ACTION_WIDTH = 88;
const OPEN_THRESHOLD = ACTION_WIDTH / 2;

/**
 * A list row that slides aside to reveal a delete button.
 *
 * Swiping is a touch idiom, so a pointer device gets the same button on hover.
 * Without that fallback the action would be unreachable with a mouse, and
 * unreachable full stop with a keyboard.
 *
 * Deleting always takes two deliberate acts: reveal, then press. That is the
 * confirmation, which is why there is no dialog on top of it.
 */
export function SwipeToDelete({ onDelete, deleteLabel = 'Löschen', busy = false, children }) {
  const [offset, setOffset] = useState(0);
  const [dragging, setDragging] = useState(false);
  const gesture = useRef(null);
  const rowRef = useRef(null);

  const open = offset <= -OPEN_THRESHOLD;

  // A second row opening, or a click anywhere else, closes this one. Two rows
  // standing open at once invites deleting the wrong one.
  useEffect(() => {
    if (offset === 0) return undefined;
    const closeOnOutside = (event) => {
      if (!rowRef.current?.contains(event.target)) setOffset(0);
    };
    document.addEventListener('pointerdown', closeOnOutside);
    return () => document.removeEventListener('pointerdown', closeOnOutside);
  }, [offset]);

  function handlePointerDown(event) {
    if (event.pointerType === 'mouse') return; // the mouse has hover instead
    gesture.current = { x: event.clientX, y: event.clientY, base: offset, axis: null };
    setDragging(true);
  }

  function handlePointerMove(event) {
    const start = gesture.current;
    if (!start) return;

    const dx = event.clientX - start.x;
    const dy = event.clientY - start.y;

    // Decide once whether this is a swipe or a scroll, and then stay out of the
    // way of the other. Re-deciding mid-gesture makes the list feel sticky.
    if (!start.axis) {
      if (Math.abs(dx) < 6 && Math.abs(dy) < 6) return;
      start.axis = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
    }
    if (start.axis !== 'x') return;

    event.preventDefault();
    setOffset(Math.max(-ACTION_WIDTH, Math.min(0, start.base + dx)));
  }

  function endGesture() {
    if (!gesture.current) return;
    gesture.current = null;
    setDragging(false);
    setOffset((current) => (current <= -OPEN_THRESHOLD ? -ACTION_WIDTH : 0));
  }

  return (
    <div ref={rowRef} className="group relative overflow-hidden rounded-lg">
      <div className="absolute inset-y-0 right-0 flex items-stretch">
        <button
          type="button"
          onClick={() => onDelete?.()}
          disabled={busy}
          tabIndex={open ? 0 : -1}
          aria-hidden={!open}
          style={{ width: ACTION_WIDTH }}
          className="flex items-center justify-center bg-red-600 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-60 transition-colors"
        >
          {deleteLabel}
        </button>
      </div>

      <div
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={endGesture}
        onPointerCancel={endGesture}
        style={{
          transform: `translateX(${offset}px)`,
          transition: dragging ? 'none' : 'transform 180ms ease-out',
          touchAction: 'pan-y',
        }}
        className="relative bg-slate-50 dark:bg-slate-800/60"
      >
        {children}
      </div>

      {/* The mouse equivalent: no swipe, so the action appears on hover. */}
      <button
        type="button"
        onClick={() => onDelete?.()}
        disabled={busy}
        title={deleteLabel}
        aria-label={deleteLabel}
        className="absolute right-2 top-1/2 hidden -translate-y-1/2 rounded-md p-1.5 text-slate-400 opacity-0 transition-opacity hover:bg-red-50 hover:text-red-600 focus-visible:opacity-100 group-hover:opacity-100 dark:hover:bg-red-900/30 dark:hover:text-red-400 sm:block"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
          />
        </svg>
      </button>
    </div>
  );
}
